import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ReactNode } from 'react';
import { useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { keyframes } from 'styled-components';

import type { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Modal } from '@sorare/core/src/atoms/Modal';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyS, BrandS } from '@sorare/core/src/atoms/typography';
import { proxyUrl } from '@sorare/core/src/atoms/ui/ResponsiveImg';
import { AnimatedIntro } from '@sorare/core/src/components/animations/AnimatedStory/AnimatedIntro';
import { HeadlinePill } from '@sorare/core/src/components/animations/AnimatedStory/HeadlinePill';
import { CardsCarousel } from '@sorare/core/src/components/landing/CollectCards/CardsCarousel';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import { formatScarcity } from '@sorare/core/src/lib/cards';
import { glossary } from '@sorare/core/src/lib/glossary';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { RarityVaultBannerWithFetch } from '#src/components/sealing/RarityVaultBanner/index.withFetch.tsx';
import { VAULT_PROGRESS_BAR_COLORS } from '#src/components/sealing/constant.ts';

import type { SealingRecap_anyCard } from './__generated__/index.graphql.ts';

const cardsEnter = keyframes`
  from {
    opacity: 0;
    transform: translateY(25%);
    }
    50% {
    opacity: 0;
    }
  to {
    opacity: 1;
    transform: translateY(0%);
  }
`;
const bannerEnter = keyframes`
  from {
    opacity: 0;
    transform: translateY(-25%);
    }
    50% {
    opacity: 0;
    }
  to {
    opacity: 1;
    transform: translateY(0%);
  }
`;

const gameweekEnter = keyframes`
  from {
    opacity: 0;
    transform: translateY(-100%);
  }
  to {
    opacity: 1;
    transform: translateY(0%);
  }
`;

const BannerContainer = styled(Vertical)`
  width: 100%;
  padding: var(--unit) var(--double-unit) 0;

  @media ${tabletAndAbove} {
    padding: var(--double-unit) 0 0;
  }
  justify-content: center;
  max-width: 100%;
  animation: ${bannerEnter} 4.5s ease-out forwards;
`;

const CardsContainer = styled(Vertical)`
  padding: 0 var(--quadruple-unit);
  margin: auto 0;
  justify-content: center;
  align-items: center;
  animation: ${cardsEnter} 2.5s ease-out forwards;
  max-height: 100%;
`;

const ButtonContainer = styled(Vertical).attrs({ center: true })`
  padding: var(--double-unit) var(--double-unit) 0;

  @media ${tabletAndAbove} {
    padding: var(--double-unit) 0 0;
  }
`;
const GameweekDateWrapper = styled(Vertical).attrs({ center: true })`
  animation: ${gameweekEnter} 5s ease-out forwards;
`;
const NoWrap = styled.span`
  white-space: nowrap;
`;

const Wrapper = styled(Vertical).attrs({ gap: 0, center: true })`
  position: fixed;
  inset: 0;
  overflow: hidden;
  background-color: var(--c-black);
  padding: var(--quadruple-unit) 0 var(--double-unit) 0;

  & > * {
    flex-grow: 1;
  }
`;
type Props = {
  rarity: Rarity;
  onClose: () => void;
  cards: SealingRecap_anyCard[];
};
export const SealingRecap = ({ rarity, cards, onClose }: Props) => {
  const [isUnmounting, setIsUnmounting] = useState(false);
  const storyRef = useRef<HTMLDivElement>(null);
  const isTabletAndAbove = useIsTabletAndAbove();

  const actionAfterOutro = (action?: () => void) => {
    setIsUnmounting(true);
    if (!action) return;
    setTimeout(action, 1000);
  };

  const sealableForSum = cards.reduce<number>((acc, card) => {
    return acc + (card.sealableFor || 0);
  }, 0);

  return (
    <Modal>
      <Wrapper ref={storyRef}>
        <AnimatedIntro
          isUnmounting={isUnmounting}
          setIsUnmounting={setIsUnmounting}
          onClose={async () => actionAfterOutro(onClose)}
          headlinePill={
            <GameweekDateWrapper>
              <HeadlinePill
                colors={{ bg: VAULT_PROGRESS_BAR_COLORS[rarity] }}
                label={
                  <BrandS>
                    <FormattedMessage
                      defaultMessage="{rarity} vault"
                      values={{ rarity: formatScarcity(rarity) }}
                    />
                  </BrandS>
                }
              />
            </GameweekDateWrapper>
          }
          title={
            <>
              {cards.length > 0 && (
                <FormattedMessage
                  defaultMessage="You sealed <nowrap>{cardsCount, plural, one {# card} other {# cards}}</nowrap>"
                  values={{
                    cardsCount: cards.length,
                    nowrap: (chunks: ReactNode[]) => {
                      return <NoWrap>{chunks}</NoWrap>;
                    },
                  }}
                />
              )}
            </>
          }
        >
          <BannerContainer>
            <RarityVaultBannerWithFetch
              rarity={rarity}
              additionalPoints={sealableForSum}
              recap
              showHowItWorksCta={false}
            />
          </BannerContainer>
          <CardsContainer>
            <CardsCarousel
              cards={cards.map(card => ({
                src: proxyUrl(card.pictureUrl || '', {
                  cropWidth: 320,
                }),
                alt: `${card.anyPlayer?.displayName}${card.rarityTyped ? ` - ${card.rarityTyped}` : ''}`,
              }))}
              slotSize={isTabletAndAbove ? 240 : 200}
            />
          </CardsContainer>
          <ButtonContainer>
            <BodyS className="text-center" color="var(--c-nd-600)">
              <FormattedMessage defaultMessage="Newly sealed cards may take a few seconds to appear in the Vault" />
            </BodyS>
            <Button
              size="medium"
              color="secondary"
              fullWidth
              onClick={async () => actionAfterOutro(onClose)}
            >
              <FormattedMessage {...glossary.continue} />
            </Button>
          </ButtonContainer>
        </AnimatedIntro>
      </Wrapper>
    </Modal>
  );
};

SealingRecap.fragments = {
  anyCard: gql`
    fragment SealingRecap_anyCard on AnyCardInterface {
      slug
      rarityTyped
      pictureUrl
      sealableFor
      anyPlayer {
        slug
        displayName
      }
    }
  ` as TypedDocumentNode<SealingRecap_anyCard>,
};
